import { byCreated, FILTER_SCHEMA, filterCtx } from "../shared";
import { Fold } from "../../ui";
import { useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { remoteRef, resolveRemote } from "../../core/remote";
import { useToasts } from "../../ui/toasts";
import { clsx } from "clsx";
import { registerViewType, type ViewProps } from "../../registry/viewTypes";
import { listTasks } from "../../core/tasks";
import { matches, type FilterAst } from "../../core/filter";
import { applyQuickAdd, snapshotOf, quickAddPropDefs } from "../../core/ops";
import { parseQuickAdd } from "../../core/quickadd";
import { useDocSelector } from "../../core/useDocSelector";
import { fmtDate, fmtMinutes, fmtTime } from "../../core/format";
import { Checkbox } from "../../ui/Checkbox";
import { QuickAdd } from "./QuickAdd";
import type { Task } from "../../core/schema";
import { notBeforeMs } from "../../core/notYet";
import { formatNotBefore, setDone } from "../../tasks/earlyDone";
import { doneBetween } from "../../core/doneOn";
import { useCircle } from "../../circle/useCircle";
import { matchesFor } from "../../core/circle";
import { partTitle, useRounds } from "../../tasks/rounds";
import { WantRow } from "../../tasks/WantRow";
import { withElsewhere } from "../elsewhere/withElsewhere";
import { SOURCE_SCHEMA, type Source } from "../elsewhere/source";

export interface ListConfig {
  filter?: FilterAst;
  sort?: { by: "createdAt" | "title"; dir: "asc" | "desc" }[];
  /** show tasks done today under the open ones */
  showDoneToday?: boolean;
  quickAdd?: boolean;
  /** "Show from" (ADR 0024): this workspace, plus what is mine elsewhere, or all my workspaces */
  source?: Source;
  /** in a shared workspace: whose tasks (`me`, `anyone`, a profile id); unset: everyone's, as before */
  for?: string;
}

function startOfToday(now: number): number {
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/**
 * The quiet right-hand side of a row: due, estimate, contexts, subtask progress. Only where the list
 * is wide (container query), so a narrow dashboard widget keeps just the titles.
 */
function RowMeta({
  task,
  steps,
  nowMs,
  lang,
}: {
  task: Task;
  steps?: { done: number; total: number };
  nowMs: number;
  lang: string;
}) {
  const { t } = useTranslation();
  const due = typeof task.props.due === "string" ? task.props.due : null;
  const est = typeof task.props.estimateMin === "number" ? task.props.estimateMin : null;
  const ctx = Array.isArray(task.props.context) ? (task.props.context as string[]) : [];
  if (!due && !est && !ctx.length && !steps) return null;
  const overdue = due !== null && Date.parse(due) < nowMs;
  const dueText =
    due === null
      ? ""
      : new Date(due).toDateString() === new Date(nowMs).toDateString()
        ? fmtTime(due, lang)
        : fmtDate(due, lang);
  return (
    <span
      // a second line under the title in a narrow column, beside it from 32 rem
      className="flex w-full shrink-0 flex-wrap items-center gap-x-3 pl-[1.875rem] text-[12px] text-neutral-500 @lg:w-auto @lg:flex-nowrap @lg:pl-0"
      data-testid="task-meta"
    >
      {ctx.slice(0, 2).map((c) => (
        <span key={c}>@{t(`enum.context.${c}`, { defaultValue: c }).replace(/^@/, "")}</span>
      ))}
      {steps && (
        <span className="font-mono tabular-nums" title={t("task.subtasks")}>
          {steps.done}/{steps.total}
        </span>
      )}
      {est !== null && <span className="font-mono tabular-nums">{fmtMinutes(est)}</span>}
      {due !== null && (
        <span
          className={clsx("tabular-nums @lg:w-[7.5rem] @lg:text-right", overdue && "text-warn")}
          title={t("props.due")}
        >
          {dueText}
        </span>
      )}
    </span>
  );
}

function Row({
  task,
  shownTitle,
  onToggle,
  onOpen,
  readOnly,
  from,
  steps,
  nowMs,
  doneNow,
}: {
  task: Task;
  /** "Task: Subtask" for rotating subtasks (ADR 0035) */
  shownTitle?: string;
  onToggle(): void;
  onOpen(): void;
  readOnly: boolean;
  /** for a not-yet task: when it starts ("from Mon 6 Oct") */
  from?: string;
  /** subtasks done / all, when the task has any */
  steps?: { done: number; total: number };
  nowMs?: number;
  /** shown as done (a repeating task done today, which stays open) */
  doneNow?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const done = task.doneAt !== null || doneNow === true;
  return (
    <li
      className="flex flex-wrap items-center gap-x-3 gap-y-0.5 px-4 py-2.5 @lg:flex-nowrap"
      data-testid="task-row"
      data-task-id={task.id}
      data-done={done}
    >
      <Checkbox
        checked={done}
        onChange={onToggle}
        disabled={readOnly}
        label={done ? t("task.undone") : t("task.done")}
      />
      <button
        type="button"
        onClick={onOpen}
        className={clsx(
          "min-w-0 flex-1 truncate text-left text-sm transition hover:text-accent",
          done
            ? "text-neutral-500 line-through decoration-neutral-300"
            : "text-neutral-900 dark:text-neutral-100",
        )}
      >
        {shownTitle || task.title || <span className="text-neutral-400">{t("task.untitled")}</span>}
      </button>
      {from && (
        <span className="shrink-0 text-[12px] text-neutral-500" data-testid="task-from">
          {t("task.from", { when: from })}
        </span>
      )}
      {!done && task.typeId === "recurring" && (
        // the want bar where it is a repeating task, as in *Wants doing*: under the title, beside it from 32 rem
        <WantRow task={task} nowMs={nowMs ?? 0} className="w-full pl-[1.875rem] @lg:w-auto @lg:pl-0" />
      )}
      {!done && <RowMeta task={task} steps={steps} nowMs={nowMs ?? 0} lang={i18n.language} />}
    </li>
  );
}

function ListView({ config, ctx, title, quietEmpty }: ViewProps<ListConfig>) {
  const { t, i18n } = useTranslation();
  const circle = useCircle(ctx.handle);
  const forWhom = config.for;
  const memberKey = circle.memberIds.join(",");
  const toast = useToasts((st) => st.push);
  const inputRef = useRef<HTMLInputElement>(null);
  const tasks = useDocSelector(ctx.handle, (doc) => listTasks(doc));
  const rounds = useRounds(ctx.handle);
  const parents = useDocSelector(ctx.handle, (doc) => {
    const s = snapshotOf(doc);
    return Object.fromEntries(Object.keys(s.tasks).map((id) => [id, s.parentOf(id)]));
  });

  const { open, later, doneToday, total } = useMemo(() => {
    // root-level tasks only unless the filter names a parent; subtasks live in the task panel
    const rootOnly = !(config.filter && "parent" in config.filter);
    // groups, sequences, parents and computed values need the structure: the same context the other widgets use
    const fctx = config.filter ? filterCtx(snapshotOf(ctx.doc), ctx.now) : undefined;
    const live = (tasks ?? []).filter(
      (x) =>
        x.archivedAt === null &&
        x.typeId !== "note" &&
        (!rootOnly || !parents?.[x.id]) &&
        matches(x, config.filter, fctx) &&
        (!forWhom ||
          !circle.shared ||
          !circle.me ||
          matchesFor(x, forWhom, circle.me, {
            shared: true,
            members: memberKey ? memberKey.split(",") : [],
            subs: rounds[x.id]?.subs,
            fair: rounds[x.id]?.fair,
          })),
    );
    const allOpen = live.filter((x) => x.doneAt === null);
    // not yet up for doing (not before a later time or set aside, inside a minimum gap): the collapsed "Later" section
    const open = allOpen.filter((x) => notBeforeMs(x, ctx.now) === null);
    const later = allOpen
      .map((x) => ({ task: x, at: notBeforeMs(x, ctx.now) }))
      .filter((x): x is { task: Task; at: number } => x.at !== null)
      .sort((a, b) => a.at - b.at);
    const since = startOfToday(ctx.now);
    const doneToday = live.filter((x) => doneBetween(x, since, since + 86_400_000));
    const sort = config.sort?.[0] ?? { by: "createdAt", dir: "asc" };
    const cmp = (a: Task, b: Task) => {
      const r =
        (sort.by === "title" ? a.title.localeCompare(b.title) : a.createdAt.localeCompare(b.createdAt)) ||
        byCreated(a, b); // equal keys keep a fixed order (§9.19)
      return sort.dir === "desc" ? -r : r;
    };
    open.sort(cmp);
    doneToday.sort((a, b) => (b.doneAt ?? "").localeCompare(a.doneAt ?? ""));
    return { open, later, doneToday, total: tasks?.length ?? 0 };
  }, [
    tasks,
    parents,
    config.filter,
    config.sort,
    ctx.now,
    forWhom,
    circle.shared,
    circle.me,
    memberKey,
    rounds,
  ]);
  // subtask progress per parent, for the row's right-hand side
  const steps = useMemo(() => {
    const out = new Map<string, { done: number; total: number }>();
    for (const x of tasks ?? []) {
      const p = parents?.[x.id];
      if (!p || x.archivedAt !== null || rounds[p]) continue;
      const cur = out.get(p) ?? { done: 0, total: 0 };
      cur.total += 1;
      if (x.doneAt !== null) cur.done += 1;
      out.set(p, cur);
    }
    return out;
  }, [tasks, parents, rounds]);

  const toggle = (task: Task) => {
    setDone(ctx.write, t, i18n.language, task, task.doneAt === null);
  };

  if (quietEmpty && open.length === 0 && doneToday.length === 0) return null;
  return (
    <div className="@container flex flex-col gap-3" data-testid="list-view">
      {title && (
        <h2 className="text-xs font-semibold uppercase tracking-[0.06em] text-neutral-500">{title}</h2>
      )}
      {config.quickAdd !== false && (
        <QuickAdd
          ref={inputRef}
          disabled={ctx.readOnly}
          showSyntaxHint={total >= 3}
          onAdd={(text) => {
            // `fj:owner/repo#12`: a task type plugin claims the prefix; the server fetches the issue
            const remote = remoteRef(text);
            if (remote) {
              resolveRemote(ctx.workspaceId, remote).then(ctx.openTask, (e: Error) =>
                toast(t(`errors.${e.message}`, { defaultValue: t("remote.failed") })),
              );
              return;
            }
            ctx.write((doc) =>
              applyQuickAdd(doc, parseQuickAdd(text, { propDefs: quickAddPropDefs(doc), now: new Date() })),
            );
          }}
        />
      )}
      {open.length === 0 && doneToday.length === 0 && later.length === 0 ? (
        <div className="rounded-lg border border-dashed border-neutral-300 bg-[var(--surface-2)] px-6 py-12 text-center text-neutral-500 dark:border-neutral-700">
          <p>{t("task.empty")}</p>
          <p className="mt-1 text-[12.5px]">{t("task.emptyHint")}</p>
        </div>
      ) : (
        <ul
          className="kss-surface divide-y divide-neutral-200 dark:divide-neutral-800"
          data-testid="task-list"
        >
          {open.map((task) => (
            <Row
              key={task.id}
              task={task}
              shownTitle={partTitle(t, rounds, task, circle.me)}
              readOnly={ctx.readOnly}
              steps={steps.get(task.id)}
              nowMs={ctx.now}
              onToggle={() => toggle(task)}
              onOpen={() => ctx.openTask(task.id)}
            />
          ))}
        </ul>
      )}
      {later.length > 0 && (
        <Fold heading label={t("task.comingUp")} testId="later-section" toggleTestId="later-toggle">
          <ul
            className="kss-surface mt-1.5 divide-y divide-neutral-200 opacity-80 dark:divide-neutral-800"
            data-testid="later-list"
          >
            {later.map(({ task, at }) => (
              <Row
                key={task.id}
                task={task}
                shownTitle={partTitle(t, rounds, task, circle.me)}
                readOnly={ctx.readOnly}
                steps={steps.get(task.id)}
                nowMs={ctx.now}
                from={formatNotBefore(at, i18n.language)}
                onToggle={() => toggle(task)}
                onOpen={() => ctx.openTask(task.id)}
              />
            ))}
          </ul>
        </Fold>
      )}
      {config.showDoneToday !== false && doneToday.length > 0 && (
        <section>
          <h3 className="mb-1.5 px-1 text-xs font-semibold uppercase tracking-[0.06em] text-neutral-500">
            {t("task.doneToday")}
          </h3>
          <ul
            className="kss-surface divide-y divide-neutral-200 dark:divide-neutral-800"
            data-testid="done-list"
          >
            {doneToday.map((task) => (
              <Row
                key={task.id}
                task={task}
                // a repeating task done today stays open: here it shows as done and cannot be unticked
                readOnly={ctx.readOnly || task.doneAt === null}
                doneNow
                steps={steps.get(task.id)}
                nowMs={ctx.now}
                onToggle={() => toggle(task)}
                onOpen={() => ctx.openTask(task.id)}
              />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

/** The list, with "Show from" (ADR 0024). */
export const ListViewEverywhere = withElsewhere(ListView);
export { ListView };

registerViewType<ListConfig>({
  id: "list",
  label: "views.list.label",
  icon: "list",
  level: "simple",
  defaultConfig: { showDoneToday: true, quickAdd: true },
  configSchema: {
    type: "object",
    properties: {
      showDoneToday: { type: "boolean", title: "config.showDoneToday" },
      quickAdd: { type: "boolean", title: "config.quickAdd" },
      source: SOURCE_SCHEMA,
      filter: FILTER_SCHEMA,
    },
  },
  Component: ListViewEverywhere,
  minSize: { w: 3, h: 3 },
});
